import { useMemo } from 'react';
import { useTheme, Box, Typography } from '@mui/material';
import Plotly from 'plotly.js';
import { DollarValuePerContract } from 'types/moviefone';
import { Plot } from '../../pages/components/Plot';
import { buildCubicSpline } from 'util/spline';

interface DollarValueChart3DProps {
  contracts: DollarValuePerContract[];
}

const MAX_STRIKES = 20;
const NUM_DTE_BINS = 10;

const DollarValueChart3D = ({ contracts }: DollarValueChart3DProps) => {
  const theme = useTheme();

  const plotConfig = useMemo(() => {
    if (contracts.length === 0) {
      return { plotData: [], layoutConfig: {} };
    }

    // Find DTE range
    let minDte = Infinity;
    let maxDte = -Infinity;
    for (const c of contracts) {
      if (c.dte < minDte) {
        minDte = c.dte;
      }
      if (c.dte > maxDte) {
        maxDte = c.dte;
      }
    }

    const numDteBins = Math.max(
      5,
      Math.min(NUM_DTE_BINS, Math.floor((maxDte - minDte) / 7)),
    );
    const dteStep = (maxDte - minDte) / (numDteBins - 1 || 1);

    // Build DTE bins
    const dteValues: number[] = [];
    for (let i = 0; i < numDteBins; i++) {
      dteValues.push(minDte + i * dteStep);
    }

    // Pre-bin contracts by DTE bin, collecting (strike, dollar_value) pairs per DTE bin
    const dteBinContracts: { strikes: number[]; values: number[] }[] = [];
    for (let i = 0; i < numDteBins; i++) {
      dteBinContracts.push({ strikes: [], values: [] });
    }

    for (const c of contracts) {
      const dteBin = Math.min(
        numDteBins - 1,
        Math.max(0, Math.round((c.dte - minDte) / dteStep)),
      );
      dteBinContracts[dteBin].strikes.push(c.strike);
      dteBinContracts[dteBin].values.push(c.dollar_value);
    }

    // Find global min/max strike across all DTE bins
    let globalMinStrike = Infinity;
    let globalMaxStrike = -Infinity;
    for (const bin of dteBinContracts) {
      for (const s of bin.strikes) {
        if (s < globalMinStrike) {
          globalMinStrike = s;
        }
        if (s > globalMaxStrike) {
          globalMaxStrike = s;
        }
      }
    }

    // Build common strike grid: 20 evenly-spaced points between min and max
    const strikeGrid: number[] = [];
    for (let i = 0; i < MAX_STRIKES; i++) {
      strikeGrid.push(
        globalMinStrike +
          (i / (MAX_STRIKES - 1)) * (globalMaxStrike - globalMinStrike),
      );
    }

    // For each DTE bin, spline-interpolate at common strike grid
    const zValues: number[][] = [];

    for (const bin of dteBinContracts) {
      // Aggregate dollar values by native strike
      const strikeSums = new Map<number, { sum: number; count: number }>();
      for (let i = 0; i < bin.strikes.length; i++) {
        const key = bin.strikes[i];
        const existing = strikeSums.get(key);
        if (existing != null) {
          existing.sum += bin.values[i];
          existing.count += 1;
        } else {
          strikeSums.set(key, { sum: bin.values[i], count: 1 });
        }
      }

      // Extract sorted native strikes and average values
      const nativeStrikes: number[] = [];
      const nativeValues: number[] = [];
      const sortedEntries = Array.from(strikeSums.entries()).sort(
        (a, b) => a[0] - b[0],
      );
      for (const [strike, { sum, count }] of sortedEntries) {
        nativeStrikes.push(strike);
        nativeValues.push(sum / count);
      }

      // Spline interpolate at common grid (need >= 2 unique strikes)
      if (nativeStrikes.length < 2) {
        zValues.push(strikeGrid.map(() => NaN));
        continue;
      }
      const spline = buildCubicSpline(nativeStrikes, nativeValues);
      const ivRow: number[] = [];
      for (const strike of strikeGrid) {
        ivRow.push(spline(strike));
      }
      zValues.push(ivRow);
    }

    // Smooth the grid (fill NaN values with neighbor averages)
    const smoothGrid = (grid: number[][]): number[][] => {
      const rows = grid.length;
      const cols = grid[0].length;
      const result = grid.map((row) => [...row]);

      for (let pass = 0; pass < 3; pass++) {
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (isNaN(result[r][c])) {
              let sum = 0;
              let count = 0;
              for (let dc = -1; dc <= 1; dc++) {
                const nc = c + dc;
                if (nc >= 0 && nc < cols && !isNaN(result[r][nc])) {
                  sum += result[r][nc];
                  count++;
                }
              }
              if (count > 0) {
                result[r][c] = sum / count;
              }
            }
          }
        }

        for (let c = 0; c < cols; c++) {
          for (let r = 0; r < rows; r++) {
            if (isNaN(result[r][c])) {
              let sum = 0;
              let count = 0;
              for (let dr = -1; dr <= 1; dr++) {
                const nr = r + dr;
                if (nr >= 0 && nr < rows && !isNaN(result[nr][c])) {
                  sum += result[nr][c];
                  count++;
                }
              }
              if (count > 0) {
                result[r][c] = sum / count;
              }
            }
          }
        }
      }

      return result;
    };

    const zValuesSmoothed = smoothGrid(zValues);

    // Determine color scale range
    const allValues = zValuesSmoothed.flat().filter((v) => !isNaN(v));
    const maxAbsValue =
      allValues.length > 0 ? Math.max(...allValues.map(Math.abs)) : 1;

    const plotData = [
      {
        type: 'surface' as const,
        x: strikeGrid,
        y: dteValues,
        z: zValuesSmoothed,
        colorscale: (theme.palette.mode === 'dark'
          ? [
              [0, '#ff1744'],
              [0.125, '#ff5252'],
              [0.25, '#ff6e6e'],
              [0.375, '#ff8a80'],
              [0.5, '#424242'],
              [0.625, '#b9f6ca'],
              [0.75, '#69f0ae'],
              [0.875, '#00e676'],
              [1, '#00c853'],
            ]
          : [
              [0, '#d32f2f'],
              [0.5, '#eeeeee'],
              [1, '#388e3c'],
            ]) as [number, string][],
        cmin: -maxAbsValue,
        cmax: maxAbsValue,
        surfacecolor: zValuesSmoothed,
        showscale: true,
        contours: {
          x: { show: false },
          y: { show: false },
          z: { show: false },
        },
        hovertemplate:
          '<b>Strike</b>: %{x:.0f}<br><b>DTE</b>: %{y:.0f}<br><b>Dollar Value</b>: $%{z:.2f}<extra></extra>',
      } as Plotly.Data,
    ];

    const layoutConfig = {
      margin: { t: 0, l: 0, r: 0, b: 0 },
      paper_bgcolor: theme.palette.background.paper,
      plot_bgcolor: theme.palette.background.paper,
      scene: {
        xaxis: {
          title: 'Strike',
          tickformat: '.0f',
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        yaxis: {
          title: 'DTE',
          tickformat: '.0f',
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        zaxis: {
          title: 'Dollar Value',
          tickformat: '$.2f',
          gridcolor: theme.palette.grey[300],
          backgroundcolor: theme.palette.background.paper,
        },
        camera: {
          eye: { x: 1.5, y: 1.5, z: 1.2 },
        },
        aspectmode: 'auto' as const,
      },
    };

    return { plotData, layoutConfig };
  }, [contracts, theme.palette]);

  if (contracts.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No dollar value data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <Plot
        data={plotConfig.plotData}
        layout={plotConfig.layoutConfig}
        style={{ width: '100%', height: '100%' }}
        config={{
          displayModeBar: false,
          scrollZoom: false,
        }}
        useResizeHandler
      />
    </Box>
  );
};

export default DollarValueChart3D;
